import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  CalendarCheck, CalendarPlus, Check, Clock, AlertTriangle, Filter, Trash2, Loader2, Sparkles, Building2, FileCheck2, X,
} from "lucide-react";
import {
  listCalendarEvents, listCompanies, seedCalendar, updateCalendarEvent,
  deleteCalendarEvent, createCalendarEvent, calendarStats, markCalendarFiling,
} from "@/lib/api";
import { toast } from "sonner";

const STATUS_FILTERS = [
  { key: "", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "overdue", label: "Overdue" },
  { key: "completed", label: "Done" },
];

export default function ComplianceCalendar() {
  const [searchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState(searchParams.get("company") || "");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [stats, setStats] = useState({ pending: 0, overdue: 0, completed: 0 });
  const [filingEvent, setFilingEvent] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [ev, cs, st] = await Promise.all([
        listCalendarEvents({
          ...(companyFilter && { company_id: companyFilter }),
          ...(statusFilter && { status: statusFilter }),
        }),
        listCompanies(),
        calendarStats(companyFilter || undefined),
      ]);
      setEvents(ev.data || []);
      setCompanies(cs.data || []);
      setStats(st.data || { pending: 0, overdue: 0, completed: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [companyFilter, statusFilter]);

  const handleSeed = async () => {
    if (!companyFilter) {
      toast.error("Pick a company first");
      return;
    }
    try {
      const res = await seedCalendar(companyFilter, 2);
      toast.success(`${res.data.created} events seeded`);
      reload();
    } catch (e) {
      toast.error("Seed failed");
    }
  };

  const handleComplete = async (ev) => {
    try {
      const newStatus = ev.status === "completed" ? "pending" : "completed";
      await updateCalendarEvent(ev.id, { status: newStatus });
      toast.success(newStatus === "completed" ? "Marked done" : "Reopened");
      reload();
    } catch {
      toast.error("Update failed");
    }
  };

  const handleDelete = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    await deleteCalendarEvent(ev.id);
    toast.success("Deleted");
    reload();
  };

  // Group by month
  const grouped = useMemo(() => {
    const groups = {};
    events.forEach((e) => {
      const m = (e.due_date || "").slice(0, 7) || "—";
      (groups[m] = groups[m] || []).push(e);
    });
    return Object.entries(groups).sort();
  }, [events]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="calendar-page">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="label-eyebrow mb-2">Statutory · Companies Act 2013</div>
          <h1 className="font-heading text-4xl font-light tracking-tight">Compliance Calendar</h1>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button
            onClick={() => setShowAdd((v) => !v)}
            className="swiss-btn-ghost"
            data-testid="add-event-toggle"
          >
            <CalendarPlus size={16} /> Add Event
          </button>
          <button
            onClick={handleSeed}
            disabled={!companyFilter}
            className="swiss-btn-primary disabled:opacity-50"
            data-testid="seed-calendar-button"
            title={companyFilter ? "" : "Select a company first"}
          >
            <Sparkles size={16} /> Auto-Seed Annual Compliance
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 mb-8 border border-zinc-200" data-testid="calendar-stats">
        <StatTile label="Overdue" value={stats.overdue} icon={AlertTriangle} accent="text-red-600" testId="stat-overdue" />
        <StatTile label="Pending" value={stats.pending} icon={Clock} accent="text-[#002FA7]" testId="stat-pending" borderLeft />
        <StatTile label="Completed" value={stats.completed} icon={Check} accent="text-green-700" testId="stat-completed" borderLeft />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 mb-6">
        <label className="block">
          <span className="label-eyebrow block mb-1.5 flex items-center gap-1"><Filter size={12} /> Company</span>
          <select
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            className="border border-zinc-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7] min-w-[220px]"
            data-testid="company-filter"
          >
            <option value="">All companies</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <div>
          <span className="label-eyebrow block mb-1.5">Status</span>
          <div className="flex border border-zinc-300" data-testid="status-filter">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setStatusFilter(f.key)}
                className={`px-4 py-2 text-sm font-medium border-r border-zinc-300 last:border-r-0 ${
                  statusFilter === f.key ? "bg-[#002FA7] text-white" : "bg-white text-zinc-700"
                }`}
                data-testid={`filter-${f.key || "all"}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {showAdd && (
        <AddEventForm companies={companies} defaultCompany={companyFilter} onAdded={() => { setShowAdd(false); reload(); }} onCancel={() => setShowAdd(false)} />
      )}

      {/* Events grouped by month */}
      {loading ? (
        <div className="text-zinc-500 py-12 text-center"><Loader2 className="inline animate-spin mr-2" size={16} /> Loading…</div>
      ) : events.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-300 bg-zinc-50 p-16 text-center" data-testid="calendar-empty">
          <CalendarCheck size={32} strokeWidth={1.25} className="mx-auto text-zinc-400 mb-4" />
          <h2 className="font-heading text-xl font-medium mb-2">No compliance events</h2>
          <p className="text-zinc-500 mb-6 text-sm">
            Select a company and click <strong>Auto-Seed Annual Compliance</strong> to populate AGM, AOC-4, MGT-7, DPT-3, MSME-1, DIR-3 KYC and board meeting due dates.
          </p>
          {!companyFilter && (
            <Link to="/companies/new" className="swiss-btn-primary text-sm" data-testid="add-company-from-calendar">
              <Building2 size={14} /> Add a company
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-8">
          {grouped.map(([month, list]) => (
            <div key={month} data-testid={`month-${month}`}>
              <div className="label-eyebrow mb-3">{formatMonth(month)} · {list.length} {list.length === 1 ? "event" : "events"}</div>
              <div className="border border-zinc-200">
                {list.map((ev, i) => (
                  <EventRow
                    key={ev.id}
                    ev={ev}
                    companies={companies}
                    isFirst={i === 0}
                    onComplete={() => handleComplete(ev)}
                    onDelete={() => handleDelete(ev)}
                    onUpdateFiling={() => setFilingEvent(ev)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <FilingMarkModal
        event={filingEvent}
        onClose={() => setFilingEvent(null)}
        onSaved={() => { setFilingEvent(null); reload(); }}
      />
    </div>
  );
}

function StatTile({ label, value, icon: Icon, accent, borderLeft, testId }) {
  return (
    <div className={`p-7 bg-white ${borderLeft ? "md:border-l border-zinc-200" : ""}`} data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="label-eyebrow">{label}</div>
        <Icon size={18} strokeWidth={1.5} className={accent} />
      </div>
      <div className={`font-heading text-5xl font-light mt-3 tracking-tight ${accent}`}>{value}</div>
    </div>
  );
}

function EventRow({ ev, companies, isFirst, onComplete, onDelete, onUpdateFiling }) {
  const company = companies.find((c) => c.id === ev.company_id);
  const isCompleted = ev.status === "completed";
  const isOverdue = ev.status === "overdue";
  const isFiled = ev.filing_status === "filed";
  const isRevision = ev.filing_status === "revision_required";
  const isExempt = ev.filing_status === "exempt";
  return (
    <div
      className={`grid grid-cols-1 md:grid-cols-[120px_1fr_auto] gap-4 p-5 ${isFirst ? "" : "border-t border-zinc-100"} ${isCompleted ? "bg-zinc-50/70" : ""}`}
      data-testid={`event-row-${ev.id}`}
    >
      {/* Date column */}
      <div>
        <div className={`font-mono-plex text-sm font-medium ${isOverdue ? "text-red-600" : "text-zinc-800"}`}>{ev.due_date}</div>
        <div className="text-xs text-zinc-500 mt-0.5">{ev.fy}</div>
        <div className="mt-2">
          {isCompleted ? (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] bg-green-700 text-white px-2 py-0.5">
              <Check size={10} /> Done
            </span>
          ) : isOverdue ? (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] bg-red-600 text-white px-2 py-0.5">
              <AlertTriangle size={10} /> Overdue
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] border border-[#002FA7] text-[#002FA7] px-2 py-0.5">
              <Clock size={10} /> Pending
            </span>
          )}
        </div>
      </div>

      {/* Title + meta */}
      <div className={isCompleted ? "opacity-60" : ""}>
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <h3 className="font-medium">{ev.title}</h3>
          <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border border-zinc-300 text-zinc-700">{ev.category}</span>
          {isFiled && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] bg-emerald-700 text-white px-2 py-0.5" data-testid={`filed-badge-${ev.id}`}>
              <FileCheck2 size={10} /> Filed
            </span>
          )}
          {isRevision && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] bg-amber-600 text-white px-2 py-0.5">
              <AlertTriangle size={10} /> Revision Required
            </span>
          )}
          {isExempt && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] bg-zinc-500 text-white px-2 py-0.5">
              Exempt
            </span>
          )}
        </div>
        {company && <div className="text-xs text-zinc-500">{company.name}</div>}
        <div className="text-xs text-zinc-600 mt-1 leading-relaxed">{ev.description}</div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-zinc-500">
          {ev.section && <span><span className="font-semibold">Sec/Rule:</span> {ev.section}</span>}
          {ev.form && <span><span className="font-semibold">Form:</span> {ev.form}</span>}
          {ev.srn && <span><span className="font-semibold">SRN:</span> <span className="font-mono-plex">{ev.srn}</span></span>}
          {ev.filed_date && <span><span className="font-semibold">Filed:</span> {ev.filed_date}</span>}
          {ev.filed_by && <span><span className="font-semibold">By:</span> {ev.filed_by}</span>}
        </div>
        {ev.filing_remarks && (
          <div className="text-[11px] text-zinc-500 italic mt-1">{ev.filing_remarks}</div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 self-start flex-wrap justify-end">
        {onUpdateFiling && (
          <button onClick={onUpdateFiling} className="swiss-btn-ghost text-xs py-1.5 px-3" data-testid={`update-filing-${ev.id}`}>
            <FileCheck2 size={12} /> {isFiled || isExempt ? "Update" : "Mark Filed"}
          </button>
        )}
        <button onClick={onComplete} className="swiss-btn-ghost text-xs py-1.5 px-3" data-testid={`toggle-complete-${ev.id}`}>
          <Check size={12} /> {isCompleted ? "Reopen" : "Mark Done"}
        </button>
        <button onClick={onDelete} className="text-zinc-400 hover:text-red-600 p-2" data-testid={`delete-event-${ev.id}`} aria-label="Delete">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

function FilingMarkModal({ event, onClose, onSaved }) {
  const [form, setForm] = useState({
    filing_status: "filed",
    filed_date: new Date().toISOString().slice(0, 10),
    srn: "",
    filed_by: "",
    filing_remarks: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (event) {
      setForm({
        filing_status: event.filing_status && event.filing_status !== "not_filed" ? event.filing_status : "filed",
        filed_date: event.filed_date || new Date().toISOString().slice(0, 10),
        srn: event.srn || "",
        filed_by: event.filed_by || "",
        filing_remarks: event.filing_remarks || "",
      });
    }
  }, [event?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!event) return null;

  const submit = async () => {
    if (form.filing_status === "filed" && !form.filed_date) {
      toast.error("Filed date is required when marking as filed");
      return;
    }
    setSaving(true);
    try {
      await markCalendarFiling(event.id, form);
      toast.success(form.filing_status === "filed" ? "Filing recorded" : "Status updated");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="filing-modal">
      <div
        className="bg-white border border-black w-full max-w-xl"
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: "8px 8px 0px 0px #0A0A0A" }}
      >
        <div className="flex items-center justify-between p-5 border-b border-zinc-200">
          <div className="flex items-center gap-2">
            <FileCheck2 size={18} className="text-[#002FA7]" />
            <h2 className="font-heading text-xl font-medium tracking-tight">Update Filing Status</h2>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900" data-testid="filing-modal-close">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-sm">
            <div className="font-medium">{event.title}</div>
            <div className="text-xs text-zinc-500 mt-0.5 font-mono-plex">
              Due {event.due_date}{event.form && ` · ${event.form}`}{event.section && ` · ${event.section}`}
            </div>
          </div>

          <FormField label="Status">
            <div className="grid grid-cols-4 gap-px bg-zinc-200 border border-zinc-200" data-testid="filing-status-tabs">
              {[
                { k: "filed", l: "Filed" },
                { k: "revision_required", l: "Revision" },
                { k: "exempt", l: "Exempt" },
                { k: "not_filed", l: "Not Filed" },
              ].map((opt) => (
                <button
                  key={opt.k}
                  type="button"
                  onClick={() => setForm({ ...form, filing_status: opt.k })}
                  className={`p-2 text-xs uppercase tracking-[0.12em] ${form.filing_status === opt.k ? "bg-[#002FA7] text-white" : "bg-white text-zinc-700"}`}
                  data-testid={`filing-status-${opt.k}`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
          </FormField>

          {form.filing_status === "filed" && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Filed Date *">
                  <input
                    type="date"
                    value={form.filed_date}
                    onChange={(e) => setForm({ ...form, filed_date: e.target.value })}
                    className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                    data-testid="filing-date"
                  />
                </FormField>
                <FormField label="SRN (MCA Service Request No.)">
                  <input
                    value={form.srn}
                    onChange={(e) => setForm({ ...form, srn: e.target.value })}
                    placeholder="e.g. AA0123456"
                    className="w-full border border-zinc-300 px-3 py-2 text-sm font-mono-plex focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                    data-testid="filing-srn"
                  />
                </FormField>
              </div>
              <FormField label="Filed By">
                <input
                  value={form.filed_by}
                  onChange={(e) => setForm({ ...form, filed_by: e.target.value })}
                  placeholder="CS / CA name"
                  className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                  data-testid="filing-by"
                />
              </FormField>
            </>
          )}

          <FormField label="Remarks">
            <textarea
              value={form.filing_remarks}
              onChange={(e) => setForm({ ...form, filing_remarks: e.target.value })}
              rows={2}
              placeholder={form.filing_status === "exempt" ? "Reason for exemption…" : "Any notes…"}
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              data-testid="filing-remarks"
            />
          </FormField>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-zinc-200">
          <button onClick={onClose} className="swiss-btn-ghost" data-testid="filing-cancel">Cancel</button>
          <button onClick={submit} disabled={saving} className="swiss-btn-primary" data-testid="filing-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddEventForm({ companies, defaultCompany, onAdded, onCancel }) {
  const [form, setForm] = useState({
    company_id: defaultCompany || (companies[0]?.id ?? ""),
    title: "",
    category: "Custom",
    section: "",
    form: "",
    due_date: new Date().toISOString().slice(0, 10),
    description: "",
    remind_days_before: 15,
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.title || !form.due_date || !form.company_id) {
      toast.error("Company, title and due date are required");
      return;
    }
    setSaving(true);
    try {
      await createCalendarEvent(form);
      toast.success("Event added");
      onAdded();
    } catch {
      toast.error("Failed to add");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-zinc-200 bg-white p-6 mb-6" data-testid="add-event-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField label="Company *">
          <select
            value={form.company_id}
            onChange={(e) => setForm({ ...form, company_id: e.target.value })}
            className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
            data-testid="event-company"
          >
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </FormField>
        <FormField label="Title *">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" data-testid="event-title" />
        </FormField>
        <FormField label="Due Date *">
          <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
            className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" data-testid="event-due" />
        </FormField>
        <FormField label="Category">
          <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" data-testid="event-category" />
        </FormField>
        <FormField label="Section / Rule">
          <input value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })}
            className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" data-testid="event-section" />
        </FormField>
        <FormField label="e-Form">
          <input value={form.form} onChange={(e) => setForm({ ...form, form: e.target.value })}
            className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" data-testid="event-form-field" />
        </FormField>
        <div className="md:col-span-2">
          <FormField label="Description">
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2} className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" data-testid="event-description" />
          </FormField>
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onCancel} className="swiss-btn-ghost" data-testid="cancel-event">Cancel</button>
        <button onClick={submit} disabled={saving} className="swiss-btn-primary" data-testid="save-event">
          {saving && <Loader2 size={14} className="animate-spin" />}
          Save Event
        </button>
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block">
      <span className="label-eyebrow block mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function formatMonth(yyyymm) {
  if (!yyyymm || yyyymm === "—") return "Undated";
  const [y, m] = yyyymm.split("-");
  const dt = new Date(Number(y), Number(m) - 1, 1);
  return dt.toLocaleString("en-US", { month: "long", year: "numeric" });
}
